import { Semester } from '@/types'
import { refreshAuthTokenAndUpdateUser } from '@/auth/utils'

const API_URL = import.meta.env.VITE_API_URL

/**
 * Fetches a list of available semesters from the backend.
 *
 * This function sends a GET request to the server to retrieve all available semesters.
 * It includes the user's ID token in the request headers for authorization.
 * An AbortSignal is passed to allow request cancellation if needed.
 *
 * @param {AbortSignal} signal - Signal to abort the request if the component unmounts.
 * @returns {Promise<Semester[]>} A promise that resolves to an array of semesters with code and friendlyName.
 * @throws {Error} Throws an error if the request fails or the server responds with an error.
 */
export const getSemestersFromDB = async (signal: AbortSignal): Promise<Semester[]> => {
  try {
    const idToken = await refreshAuthTokenAndUpdateUser()
    const response = await fetch(`${API_URL}/api/semesters/list`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      signal,
    })

    if (!response.ok) {
      const body = await response.json()
      console.log('Error fetching semesters:', body)
      throw new Error(body.message || 'Failed to fetch semesters')
    }

    const data = await response.json()
    return data.semesters || []
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }
    throw new Error('An error occurred while fetching semesters.')
  }
}
